import { nanoid } from 'nanoid'
import { create } from 'zustand'
import {
  MessageRole,
  AgentStatus as PersistedAgentStatus,
  AgentAttachmentType,
  type AgentChat,
  type AgentMessage,
  type AgentToolCall,
  type AgentArtifact,
  type AgentAttachment
} from '@multiplayer-app/ai-agent-types'

const isPageContextAttachment = (a: AgentAttachment): boolean =>
  a.type === AgentAttachmentType.Context && (a as any)?.metadata?.kind === 'pageContext'

export interface QueuedComposerMessage {
  id: string
  content: string
  attachments?: AgentAttachment[]
  createdAt: string
}

export enum UiChatStatus {
  Idle = 'idle',
  Sending = 'sending',
  Streaming = 'streaming'
}

export const mapPersistedStatusToUiStatus = (status?: PersistedAgentStatus): UiChatStatus => {
  if (
    status === PersistedAgentStatus.Streaming ||
    status === PersistedAgentStatus.Processing ||
    status === PersistedAgentStatus.WaitingForUserAction
  )
    return UiChatStatus.Streaming

  return UiChatStatus.Idle
}

export interface ChatStateEntry {
  reasoning: string[]
  messages: AgentMessage[]
  artifacts: AgentArtifact[]
  pendingAssistant: AgentMessage | undefined
  messageQueue: QueuedComposerMessage[]
  composerDraft: { value: string; attachments: AgentAttachment[] }
  composerError: string | undefined
  pageContextDismissed: boolean
  status: UiChatStatus
}

/** Default transient state for a chat. Use when reading chatState[key] that may be undefined. */
export const defaultChatState = (): ChatStateEntry => ({
  messages: [],
  reasoning: [],
  artifacts: [],
  pendingAssistant: undefined,
  messageQueue: [],
  composerDraft: { value: '', attachments: [] },
  composerError: undefined,
  pageContextDismissed: false,
  status: UiChatStatus.Idle
})

export interface AgentStoreState {
  contextKey: string
  activeChatId?: string
  draftChatKey: string
  chats: Record<string, AgentChat>
  chatState: Record<string, ChatStateEntry>
  isChatHistoryOpen: boolean
  composer: {
    model?: string
    temperature: number
  }
}

export interface AgentStoreActions {
  setContextKey: (contextKey: string) => void
  setActiveChat: (chatId?: string) => void
  upsertChat: (chat: AgentChat) => void
  upsertChatState: (chatId: string, state: ChatStateEntry | ((prev: ChatStateEntry) => ChatStateEntry)) => void
  hydrateChats: (chats: AgentChat[]) => void
  removeChat: (chatId: string) => void
  pushReasoning: (chatId: string, fragment: string) => void
  setArtifacts: (chatId: string, artifacts: AgentArtifact[]) => void
  setComposerOptions: (options: Partial<AgentStoreState['composer']>) => void
  setComposerDraft: (chatKey: string, draft: { value: string; attachments: AgentAttachment[] }) => void
  setComposerValue: (chatKey: string, value: string) => void
  addComposerAttachments: (chatKey: string, attachments: AgentAttachment[]) => void
  removeComposerAttachment: (chatKey: string, attachmentId: string) => void
  clearComposerDraft: (chatKey: string) => void
  setComposerError: (chatKey: string, error?: string) => void
  setPageContextDismissed: (chatKey: string, dismissed: boolean) => void
  setChatStatus: (chatKey: string, status: UiChatStatus) => void
  setPendingAssistant: (chatKey: string, message?: AgentMessage) => void
  commitPendingAssistant: (chatId: string) => void
  appendSystemMessage: (chatId: string, content: string) => void
  upsertToolCall: (chatId: string, call: AgentToolCall) => void
  enqueueQueuedMessage: (chatKey: string, content: string, attachments?: AgentAttachment[]) => void
  removeQueuedMessage: (chatKey: string, id: string) => void

  moveChatKeyState: (fromChatKey: string, toChatKey: string) => void
  setChatHistoryOpen: (isOpen: boolean) => void
  toggleChatHistory: () => void
  setChatMessages: (chatId: string, messages: AgentMessage[] | ((prev: AgentMessage[]) => AgentMessage[])) => void
  appendMessagesToChat: (chatId: string, messages: AgentMessage[]) => void
}

export type AgentStore = AgentStoreState & AgentStoreActions

/**
 * Shared immutable default entry. Returning the same instance (instead of a
 * fresh object per call) keeps selector results referentially stable for chats
 * without state, so store updates don't re-render their subscribers.
 */
const EMPTY_CHAT_STATE: ChatStateEntry = Object.freeze(defaultChatState()) as ChatStateEntry

/** Get transient chat state, with defaults when the key has no entry. */
export const getChatState = (state: AgentStoreState, key: string): ChatStateEntry =>
  state.chatState[key] ?? EMPTY_CHAT_STATE

export const createAgentStore = (initialContextKey: string, getNewChatModel?: () => string | undefined) =>
  create<AgentStore>()((set, get) => ({
    contextKey: initialContextKey,
    activeChatId: undefined,
    draftChatKey: `draft-${nanoid()}`,
    chats: {},
    chatState: {},
    isChatHistoryOpen: false,
    composer: { temperature: 0.4 },
    setContextKey: (contextKey) => set({ contextKey }),
    setActiveChat: (activeChatId) => {
      set((state) => {
        // When selecting an existing chat, switch contextKey to match the chat's contextKey
        // When creating a new chat (undefined), keep the current contextKey
        let newContextKey = state.contextKey
        let chatModel: string | undefined = undefined
        if (activeChatId) {
          const chat = state.chats[activeChatId]
          if (chat?.contextKey) {
            newContextKey = chat.contextKey
          }
          chatModel = chat?.model
        }

        const isSwitchingToDifferentChat = state.activeChatId !== activeChatId
        // If switching to "new chat" (undefined), reset composer.model to the user's saved
        // preference (Auto unless they explicitly picked one). Carrying over the previous
        // chat's model would re-send a backend-stamped default as an explicit user pick,
        // freezing it into every future chat even after the admin changes the default.
        // If selecting an existing chat, sync composer.model only when switching to a
        // *different* chat. When staying on the same chat (e.g. stream completion), don't
        // overwrite composer—the user may have changed the model during streaming.
        let composer = state.composer
        if (!activeChatId) {
          composer = { ...state.composer, model: getNewChatModel?.() }
        } else if (isSwitchingToDifferentChat) {
          composer = { ...state.composer, model: chatModel }
        }
        const update: Partial<AgentStoreState> = {
          activeChatId,
          contextKey: newContextKey,
          composer
        }
        return update
      })
    },
    upsertChat: (chat) =>
      set((state) => {
        const existingChat = state.chats[chat.id]
        const isActiveChat = state.activeChatId === chat.id
        const shouldSyncModel = isActiveChat && (!existingChat || existingChat.model !== chat.model)

        return {
          chats: { ...state.chats, [chat.id]: chat },
          ...(shouldSyncModel ? { composer: { ...state.composer, model: chat.model } } : {})
        }
      }),
    upsertChatState: (chatId, payload) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        const newState = typeof payload === 'function' ? payload(prev) : payload
        const nextState = { ...prev, ...newState }
        return {
          chatState: { ...state.chatState, [chatId]: nextState }
        }
      }),
    hydrateChats: (chats) =>
      set((state) => {
        // Replace chats with server truth (critical so deletions actually remove items).
        const nextChats: Record<string, AgentChat> = {}
        chats.forEach((chat) => {
          nextChats[chat.id] = chat
        })

        // Preserve activeChatId if set (e.g. restored from storage, may be on unfetched page).
        // useEnsureActiveChat will fetch if missing; 404 clears it.
        const newActiveChatId = state.activeChatId ?? chats[0]?.id

        // When active chat was fetched by ID (not in paginated list), keep it so messages load.
        const activeFetchedById = newActiveChatId && !nextChats[newActiveChatId] && state.chats[newActiveChatId]
        if (activeFetchedById) {
          nextChats[newActiveChatId] = state.chats[newActiveChatId]
        }

        const activeChat = newActiveChatId ? nextChats[newActiveChatId] : undefined
        return {
          chats: nextChats,
          activeChatId: newActiveChatId,
          composer: { ...state.composer, model: activeChat?.model }
        }
      }),
    removeChat: (chatId) =>
      set((state) => {
        if (!state.chats[chatId]) return state
        const { [chatId]: _removedChat, ...restChats } = state.chats
        const { [chatId]: _removedState, ...restChatState } = state.chatState
        const isActive = state.activeChatId === chatId
        return {
          chats: restChats,
          chatState: restChatState,
          ...(isActive ? { activeChatId: undefined } : {})
        }
      }),
    pushReasoning: (chatId, fragment) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        return {
          chatState: {
            ...state.chatState,
            [chatId]: { ...prev, reasoning: [...prev.reasoning, fragment] }
          }
        }
      }),
    setArtifacts: (chatId, artifacts) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        return {
          chatState: {
            ...state.chatState,
            [chatId]: { ...prev, artifacts }
          }
        }
      }),
    setComposerOptions: (options) =>
      set((state) => ({
        composer: {
          ...state.composer,
          ...options
        }
      })),
    setComposerDraft: (chatKey, draft) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: {
              ...prev,
              composerDraft: { value: draft.value ?? '', attachments: draft.attachments ?? [] }
            }
          }
        }
      }),
    setComposerValue: (chatKey, value) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: {
              ...prev,
              composerDraft: { ...prev.composerDraft, value }
            }
          }
        }
      }),
    addComposerAttachments: (chatKey, attachments) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        const current = prev.composerDraft.attachments ?? []
        if (!attachments || attachments.length === 0) return state

        // Ensure we never end up with duplicate page context chips (e.g. React StrictMode effects).
        const existingPage = current.find(isPageContextAttachment)
        const incomingPage = attachments.find(isPageContextAttachment)
        if (incomingPage) {
          const restIncoming = attachments.filter((a) => !isPageContextAttachment(a))
          const withoutExistingPage = current.filter((a) => !isPageContextAttachment(a))
          const upsertedPage: AgentAttachment = existingPage ? { ...incomingPage, id: existingPage.id } : incomingPage
          const merged = [upsertedPage, ...restIncoming, ...withoutExistingPage]
          const byId = new Map<string, AgentAttachment>()
          for (const att of merged) byId.set(att.id, att)
          return {
            chatState: {
              ...state.chatState,
              [chatKey]: {
                ...prev,
                composerDraft: { ...prev.composerDraft, attachments: Array.from(byId.values()) }
              }
            }
          }
        }

        const merged = [...attachments, ...current]
        const byId = new Map<string, AgentAttachment>()
        for (const att of merged) byId.set(att.id, att)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: {
              ...prev,
              composerDraft: { ...prev.composerDraft, attachments: Array.from(byId.values()) }
            }
          }
        }
      }),
    removeComposerAttachment: (chatKey, attachmentId) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: {
              ...prev,
              composerDraft: {
                ...prev.composerDraft,
                attachments: prev.composerDraft.attachments.filter((a) => a.id !== attachmentId)
              }
            }
          }
        }
      }),
    clearComposerDraft: (chatKey) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: {
              ...prev,
              composerDraft: { value: '', attachments: [] }
            }
          }
        }
      }),
    setComposerError: (chatKey, error) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: { ...prev, composerError: error }
          }
        }
      }),
    setPageContextDismissed: (chatKey, dismissed) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: { ...prev, pageContextDismissed: dismissed }
          }
        }
      }),
    setChatStatus: (chatKey, status) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: { ...prev, status }
          }
        }
      }),
    setPendingAssistant: (chatKey, message) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: { ...prev, pendingAssistant: message }
          }
        }
      }),
    commitPendingAssistant: (chatId) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        const pending = prev.pendingAssistant
        if (!pending) return state

        const chat = state.chats[chatId]
        if (!chat) {
          return {
            chatState: {
              ...state.chatState,
              [chatId]: { ...prev, pendingAssistant: undefined }
            }
          }
        }

        const hasContent = typeof pending.content === 'string' && pending.content.trim().length > 0
        const nextMessages = hasContent
          ? (() => {
              const last = prev.messages[prev.messages.length - 1]
              if (last?.id === pending.id) return prev.messages
              const message: AgentMessage = { ...pending, chat: chatId }
              return [...prev.messages, message]
            })()
          : prev.messages

        return {
          chatState: {
            ...state.chatState,
            [chatId]: {
              ...prev,
              messages: nextMessages,
              pendingAssistant: undefined
            }
          }
        }
      }),
    enqueueQueuedMessage: (chatKey, content, attachments) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        const entry: QueuedComposerMessage = {
          id: nanoid(),
          content,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
          createdAt: new Date().toISOString()
        }
        return {
          chatState: {
            ...state.chatState,
            [chatKey]: { ...prev, messageQueue: [...prev.messageQueue, entry] }
          }
        }
      }),
    removeQueuedMessage: (chatKey, id) =>
      set((state) => {
        const prev = getChatState(state, chatKey)
        if (prev.messageQueue.length === 0) return state
        const next = prev.messageQueue.filter((item) => item.id !== id)
        return {
          chatState: { ...state.chatState, [chatKey]: { ...prev, messageQueue: next } }
        }
      }),
    moveChatKeyState: (fromChatKey, toChatKey) =>
      set((state) => {
        if (fromChatKey === toChatKey) return state

        const from = getChatState(state, fromChatKey)
        const to = getChatState(state, toChatKey)

        const hasAnything =
          from.messageQueue.length > 0 ||
          !!from.pendingAssistant ||
          from.status !== UiChatStatus.Idle ||
          from.composerDraft.value.length > 0 ||
          from.composerDraft.attachments.length > 0 ||
          !!from.composerError

        if (!hasAnything) return state

        const fromHasDraft = from.composerDraft.value.length > 0 || from.composerDraft.attachments.length > 0
        const toHasDraft = to.composerDraft.value.length > 0 || to.composerDraft.attachments.length > 0

        const merged: ChatStateEntry = {
          messages: to.messages,
          reasoning: to.reasoning,
          artifacts: to.artifacts,
          pendingAssistant: from.pendingAssistant ?? to.pendingAssistant,
          messageQueue: [...to.messageQueue, ...from.messageQueue],
          composerDraft: fromHasDraft && !toHasDraft ? from.composerDraft : to.composerDraft,
          composerError: from.composerError ?? to.composerError,
          pageContextDismissed: state.chatState[toChatKey] ? to.pageContextDismissed : from.pageContextDismissed,
          status: from.status ?? to.status
        }

        return {
          chatState: {
            ...state.chatState,
            [toChatKey]: merged,
            [fromChatKey]: defaultChatState()
          }
        }
      }),
    appendSystemMessage: (chatId, content) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        const message: AgentMessage = {
          id: nanoid(),
          chat: chatId,
          content,
          role: MessageRole.System,
          createdAt: new Date().toISOString()
        }
        return {
          chatState: {
            ...state.chatState,
            [chatId]: { ...prev, messages: [...prev.messages, message] }
          }
        }
      }),
    upsertToolCall: (chatId, call) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        const messages = [...prev.messages]
        const existingIndex = messages.findIndex(
          (msg) => msg.role === MessageRole.Tool && msg.toolCalls?.some((tool) => tool.id === call.id)
        )
        if (existingIndex >= 0) {
          const existingMessage = messages[existingIndex]
          const nextToolCalls = (existingMessage.toolCalls ?? []).map((tool) => (tool.id === call.id ? call : tool))
          messages[existingIndex] = {
            ...existingMessage,
            toolCalls: nextToolCalls
          }
        } else {
          messages.push({
            id: `tool-${call.id}`,
            content: '',
            chat: chatId,
            toolCalls: [call],
            role: MessageRole.Tool,
            createdAt: new Date().toISOString()
          })
        }
        return {
          chatState: {
            ...state.chatState,
            [chatId]: { ...prev, messages }
          }
        }
      }),
    setChatHistoryOpen: (isOpen) => set({ isChatHistoryOpen: isOpen }),
    toggleChatHistory: () =>
      set((state) => ({
        isChatHistoryOpen: !state.isChatHistoryOpen
      })),
    setChatMessages: (chatId, payload) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        const prevMessages = prev.messages ?? []
        const next = typeof payload === 'function' ? payload(prevMessages) : payload
        return {
          chatState: { ...state.chatState, [chatId]: { ...prev, messages: next } }
        }
      }),
    appendMessagesToChat: (chatId, messages) =>
      set((state) => {
        const prev = getChatState(state, chatId)
        if (messages.length === 0) return state
        const existingIds = new Set(messages.map((m) => m.id))
        const oldMessages = prev.messages.filter((m) => !existingIds.has(m.id))
        const nextMessages = [...oldMessages, ...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        return {
          chatState: {
            ...state.chatState,
            [chatId]: { ...prev, messages: nextMessages }
          }
        }
      })
  }))
